import {
  Form,
  Section,
  TextField,
  RenderFuncProps,
  DateField,
  SectionDivider,
} from '@dispatch/web/ui';
import { Box, Button, Grid, Stack } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Control, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import styled from '@emotion/styled';
import {
  CertsAndLicenseEdit,
  ResultStatus,
  StateProvince,
} from '@dispatch/shared/types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const StyledH1 = styled.h1`
  margin: 0;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: ${grey[700]};
`;

export const CertsAndLicenseModalEdit = ({
  upsertResourceStatus,
  editData,
  formErrors,
  formControl,
  handleSubmit,
  onSubmit,
  clearData,
}: {
  upsertResourceStatus?: ResultStatus;
  editData: CertsAndLicenseEdit | null;
  formErrors: FieldErrors;
  formControl: Control<CertsAndLicenseEdit, any>;
  handleSubmit: UseFormHandleSubmit<CertsAndLicenseEdit>;
  onSubmit: () => void;
  clearData: () => void;
}) => {
  const formArgs = (editData: CertsAndLicenseEdit | null) => ({
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Stack data-testid="add-edit-cert-modal">
          <Box>
            <Stack paddingBottom={2}>
              <Stack
                direction="row"
                gap={1}
                marginBottom={2}
                alignItems="center"
              >
                <StyledH1>Selected:</StyledH1>
                <div>{editData?.verificationType}</div>
              </Stack>
              <Grid container marginBottom={3}>
                <Grid item xs={6}>
                  <Stack>
                    <StyledH1>Name</StyledH1>
                    <div>{editData?.name}</div>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack>
                    <StyledH1>Organization</StyledH1>
                    <div>{editData?.organization}</div>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6}>
                  <Stack>
                    <StyledH1>Description</StyledH1>
                    <div>{editData?.description}</div>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack>
                    <StyledH1>Applies to States</StyledH1>
                    <div>
                      {editData?.stateProvince
                        ?.map((data: StateProvince) => data?.state)
                        .join(', ')}
                    </div>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
            <SectionDivider />
            <Section title={`Details`}>
              <Grid container spacing={2} marginBottom={2}>
                <Grid item xs={12}>
                  <TextField
                    id="licenseNumber"
                    name="licenseNumber"
                    label={`${editData?.verificationType || ''} Number`}
                    required
                    control={control}
                    errors={errors}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              </Grid>
              <Grid container marginBottom={2}>
                <Grid item xs={12}>
                  <Stack direction="row" gap={2}>
                    <DateField
                      errors={errors}
                      popperProps={{ sx: { zIndex: 10000 } }}
                      id={`issueDate`}
                      name={`issueDate`}
                      label="Issue Date"
                      control={control}
                    />
                    <DateField
                      errors={editData?.requireExpirationDate ? errors : null}
                      popperProps={{ sx: { zIndex: 10000 } }}
                      id={`expirationDate`}
                      name={`expirationDate`}
                      label="Expiry Date"
                      control={control}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Section>
          </Box>
        </Stack>
      );
    },
  });

  return (
    <Stack justifyContent="space-between" height="550px" p={2}>
      <div>
        <Form<CertsAndLicenseEdit>
          {...formArgs(editData)}
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
        />
      </div>
      <Stack direction="row" justifyContent="space-between">
        {editData?.mode === 'assign' && (
          <Button
            variant="outlined"
            sx={{
              fontWeight: 700,
              fontSize: '0.8125rem',
              lineHeight: '22px',
              letterSpacing: '0.46px',
            }}
            onClick={clearData}
            startIcon={<ChevronLeftIcon />}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          disabled={upsertResourceStatus === 'loading'}
          color="info"
          sx={{
            backgroundColor: '#2196F3',
            fontWeight: 700,
            fontSize: '0.8125rem',
            lineHeight: '22px',
            color: 'white',
            letterSpacing: '0.46px',
          }}
          onClick={() => onSubmit()}
        >
          {upsertResourceStatus === 'loading' ? 'Saving' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );
};
